import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import formatMessage from 'format-message'
import { Form } from 'react-bootstrap'
import DrawerHeader from './DrawerHeader'

/**
 * DrawerGlossary - Simple glossary component for the drawer
 */
const DrawerGlossary = ({ coursenum, userEmail }) => {
  const hasUser = !!(userEmail && String(userEmail).trim())
  const [terms, setTerms] = useState([])
  const [filteredTerms, setFilteredTerms] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const searchInputRef = React.useRef(null)

  useEffect(() => {
    if (!hasUser) {
      setLoading(false)
      return
    }

    let cancelled = false
    async function loadTerms() {
      try {
        setLoading(true)
        setError(null)
        const resp = await fetch(`/reader/glossary.terms/${coursenum}/`, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
          },
          credentials: 'same-origin',
        })
        if (!resp.ok) {
          throw new Error(`HTTP ${resp.status}`)
        }
        const data = await resp.json()
        const items = (data && Array.isArray(data.terms)) ? data.terms : []
        if (!cancelled) {
          setTerms(items)
          setFilteredTerms(items)
        }
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load terms')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    if (coursenum) {
      loadTerms()
    }
    return () => {
      cancelled = true
    }
  }, [coursenum, hasUser])

  useEffect(() => {
    const norm = v => (v || '').toString().toLowerCase()
    const q = norm(searchTerm)
    if (q) {
      const filtered = terms
        .filter(item => {
          const t = norm(item && item.term)
          const d = norm(item && item.definition)
          return t.indexOf(q) !== -1 || d.indexOf(q) !== -1
        })
        .sort((a, b) => {
          const ta = norm(a && a.term)
          const da = norm(a && a.definition)
          const tb = norm(b && b.term)
          const db = norm(b && b.definition)
          const aTermMatch = ta.indexOf(q) !== -1
          const bTermMatch = tb.indexOf(q) !== -1
          if (aTermMatch !== bTermMatch) return aTermMatch ? -1 : 1
          const aPos = aTermMatch ? ta.indexOf(q) : da.indexOf(q)
          const bPos = bTermMatch ? tb.indexOf(q) : db.indexOf(q)
          if (aPos !== bPos) return aPos - bPos
          return ta.localeCompare(tb)
        })
      setFilteredTerms(filtered)
    } else {
      setFilteredTerms(terms)
    }
  }, [searchTerm, terms])

  useEffect(() => {
    // Focus on mount (since this component is conditionally rendered)
    if (hasUser && searchInputRef.current) {
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus()
        }
      }, 400)
    }
  }, [hasUser])

  useEffect(() => {
    const handleDrawerStateChange = (event) => {
      const activeDrawer = event && event.detail && event.detail.activeDrawer
      if (activeDrawer === 'glossary' && hasUser && searchInputRef.current) {
        setTimeout(() => {
          if (searchInputRef.current) {
            searchInputRef.current.focus()
          }
        }, 400)
      }
    }

    document.addEventListener('drawer-state-change', handleDrawerStateChange)
    return () => {
      document.removeEventListener('drawer-state-change', handleDrawerStateChange)
    }
  }, [hasUser])

  return (
    <div className="drawer-glossary">
      <DrawerHeader title={formatMessage('Glossary')} />

      {!hasUser ? (
        <div className="drawer-empty-state p-3">
          <p className="text-muted text-p-margin-removed">
            {formatMessage('You must be logged in to use the glossary.')}
          </p>
        </div>
      ) : (
        <React.Fragment>
          <div className="drawer-search-input">
            <Form.Control
              ref={searchInputRef}
              type="text"
              placeholder={formatMessage('Search glossary terms...')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="glossary-terms" role="list" aria-label={formatMessage('Glossary terms list')}>
            {loading ? (
              <div className="drawer-loading">
                <i className="fa fa-spinner fa-spin" aria-hidden="true"></i>
                <span className="sr-only">Loading glossary terms...</span>
              </div>
            ) : error ? (
              <div className="error-message drawer-section">
                {formatMessage('Error loading glossary: {msg}', { msg: error })}
              </div>
            ) : (
              <React.Fragment>
                {searchTerm && filteredTerms.length > 0 && (
                  <div role="status" aria-live="polite" className="sr-only">
                    {formatMessage('Found {count} {label}', {
                      count: filteredTerms.length,
                      label: filteredTerms.length === 1 ? formatMessage('term') : formatMessage('terms')
                    })}
                  </div>
                )}
                {filteredTerms.length > 0 ? (
          filteredTerms.map((item, index) => (
            <div
              key={index}
              className="glossary-term"
              role="listitem"
              aria-labelledby={`gloss-term-${index}`}
              aria-describedby={`gloss-def-${index}`}
            >
              <p
                id={`gloss-term-${index}`}
                className="glossary-term-title"
                style={{ margin: '8px 0 6px 0', padding: 0, fontWeight: 600, display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}
                aria-label={formatMessage('Glossary term: {term}', { term: item.term })}
              >
                <span>{item.term}</span>
                {item.url && (
                  <a
                    href={item.url}
                    className="glossary-term-link btn btn-myed btn-link-myed btn-xs-myed"
                    aria-label={formatMessage('View {term} in book', { term: item.term })}
                  >
                    {formatMessage('View')}
                    <i className="fa-light fa-external-link-alt ml-2" aria-hidden="true"></i>
                  </a>
                )}
              </p>
              <p
                id={`gloss-def-${index}`}
                className="glossary-term-definition"
                aria-label={formatMessage('Definition: {definition}', { definition: item.definition })}
              >
                {item.definition}
              </p>
            </div>
          ))
        ) : (
          <div className="drawer-no-results" role="status" aria-live="polite">
            {formatMessage('No glossary terms found matching "{search}"', { search: searchTerm })}
          </div>
        )}
              </React.Fragment>
            )}
          </div>
        </React.Fragment>
      )}
    </div>
  )
}

DrawerGlossary.propTypes = {
  coursenum: PropTypes.string.isRequired,
  userEmail: PropTypes.string
}

export default DrawerGlossary
